import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import { 
    FaChartLine, FaCalendarAlt, FaFileDownload, FaPrint, 
    FaPercentage, FaMoneyBillWave 
} from 'react-icons/fa';
import { 
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell 
} from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import './RelatoriosFinanceiros.css';

const formatCurrency = (val) => val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const formatPercent = (val) => `${val.toFixed(1)}%`;

export default function RelatoriosFinanceiros() {
    const [ano, setAno] = useState(new Date().getFullYear());
    const [dados, setDados] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        carregarDados();
    }, [ano]);

    const carregarDados = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/api/financeiro/relatorios/anual?ano=${ano}`);
            setDados(data);
        } catch (error) {
            console.error("Erro ao carregar DRE:", error);
        } finally {
            setLoading(false);
        }
    };

    // --- MOTOR DE CÁLCULO DA DRE ---
    const dreData = useMemo(() => {
        if (!dados) return [];
        
        const receitaTotal = dados.receita_bruta || 1; // Evita divisão por zero
        
        // Filtra e ordena despesas
        const despesasCats = (dados.detalhamento || [])
            .filter(d => d.tipo_categoria === 'Despesa' || !d.tipo_categoria)
            .sort((a, b) => parseFloat(b.valor_total) - parseFloat(a.valor_total));
        
        // Monta estrutura Contábil Hierárquica
        return [
            { 
                label: "Receita Operacional Bruta", 
                valor: dados.receita_bruta, 
                tipo: 'receita', 
                av: 100, 
                destaque: true 
            },
            { 
                label: "(-) Deduções / Impostos", 
                valor: 0, // Placeholder para futura integração fiscal
                tipo: 'deducao', 
                av: 0,
                destaque: false 
            },
            { 
                label: "(=) Receita Líquida", 
                valor: dados.receita_bruta, 
                tipo: 'total', 
                av: 100,
                destaque: true 
            },
            // Mapeia cada categoria de despesa como uma linha
            ...despesasCats.map(d => ({
                label: `(-) ${d.categoria || 'Despesas Gerais'}`,
                valor: parseFloat(d.valor_total),
                tipo: 'despesa',
                av: (parseFloat(d.valor_total) / receitaTotal) * 100,
                destaque: false
            })),
            { 
                label: "(=) Resultado do Exercício (Lucro/Prejuízo)", 
                valor: dados.lucro_liquido, 
                tipo: dados.lucro_liquido >= 0 ? 'lucro' : 'prejuizo', 
                av: (dados.lucro_liquido / receitaTotal) * 100,
                destaque: true 
            }
        ];
    }, [dados]);

    const handlePrint = () => {
        window.print();
    };

    const handleExportPDF = () => {
        const doc = new jsPDF();
        
        // Cabeçalho do PDF
        doc.setFontSize(18);
        doc.setTextColor(16, 28, 93); // Azul System Hub
        doc.text(`DRE Gerencial - Exercício ${ano}`, 14, 20);
        
        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text(`Gerado em: ${new Date().toLocaleDateString()}`, 14, 26);
        
        const tableData = dreData.map(row => [
            row.label,
            formatCurrency(row.valor),
            row.av.toFixed(1) + '%'
        ]);

        autoTable(doc, {
            head: [['Descrição', 'Valor (R$)', 'AV (%)']],
            body: tableData,
            startY: 35,
            theme: 'grid',
            headStyles: { fillColor: [16, 28, 93], textColor: 255, fontStyle: 'bold' },
            columnStyles: {
                0: { cellWidth: 'auto' },
                1: { halign: 'right', fontStyle: 'bold' },
                2: { halign: 'center' }
            },
            // Estiliza linhas de totais
            didParseCell: function (data) {
                if (data.row.raw[0].includes('(=)') || data.row.raw[0].includes('Receita Operacional')) {
                    data.cell.styles.fontStyle = 'bold';
                    data.cell.styles.fillColor = [240, 242, 245];
                }
            }
        });

        // Adiciona Resumo ao final
        const finalY = doc.lastAutoTable.finalY + 15;
        doc.setFontSize(12);
        doc.setTextColor(0);
        doc.text(`Resultado Final: ${formatCurrency(dados.lucro_liquido)}`, 14, finalY);

        doc.save(`DRE_${ano}.pdf`);
    };

    return (
        <div className="relatorios-page">
            <header className="relatorios-header no-print">
                <div className="header-left">
                    <h1><FaChartLine /> DRE Gerencial</h1>
                    <p>Demonstração do Resultado do Exercício de {ano}</p>
                </div>
                <div className="header-controls">
                    <div className="year-selector">
                        <FaCalendarAlt />
                        <select value={ano} onChange={(e) => setAno(e.target.value)}>
                            {/* Gera anos dinamicamente: ano atual - 2 até ano atual + 1 */}
                            {Array.from({ length: 4 }, (_, i) => new Date().getFullYear() - 2 + i).map(y => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </select>
                    </div>
                    <button onClick={handlePrint} className="btn-action print"><FaPrint /> Imprimir</button>
                    <button onClick={handleExportPDF} className="btn-action export"><FaFileDownload /> PDF</button>
                </div>
            </header>

            {loading ? (
                <div className="loading-area"><div className="spinner"></div> Consolidando dados contábeis...</div>
            ) : (
                <div className="relatorio-content">
                    
                    {/* KPIS TOPO (Cartões de Destaque) */}
                    <div className="kpi-grid-dre no-print">
                        <div className="kpi-card-dre">
                            <div className="kpi-icon blue"><FaMoneyBillWave /></div>
                            <div className="kpi-data">
                                <span>Receita Bruta</span>
                                <strong>{formatCurrency(dados.receita_bruta)}</strong>
                            </div>
                        </div>
                        <div className="kpi-card-dre">
                            <div className="kpi-icon red"><FaChartLine /></div>
                            <div className="kpi-data">
                                <span>Custos Totais</span>
                                <strong>{formatCurrency(dados.total_despesas)}</strong>
                            </div>
                        </div>
                        <div className="kpi-card-dre">
                            <div className="kpi-icon green"><FaPercentage /></div>
                            <div className="kpi-data">
                                <span>Margem Líquida</span>
                                <strong className={dados.lucro_liquido >= 0 ? 'text-green' : 'text-red'}>
                                    {dados.receita_bruta > 0 
                                        ? formatPercent((dados.lucro_liquido / dados.receita_bruta) * 100) 
                                        : '0%'}
                                </strong>
                            </div>
                        </div>
                        <div className="kpi-card-dre result">
                            <div className="kpi-data center">
                                <span>Resultado do Exercício</span>
                                <strong className={dados.lucro_liquido >= 0 ? 'text-white' : 'text-red-light'}>
                                    {formatCurrency(dados.lucro_liquido)}
                                </strong>
                            </div>
                        </div>
                    </div>

                    <div className="dre-layout">
                        {/* TABELA DRE (O "Papel Contábil") */}
                        <div className="dre-paper">
                            <div className="paper-header">
                                <h3>Demonstrativo Contábil</h3>
                                <span className="badge-year">Exercício {ano}</span>
                            </div>
                            <table className="dre-table">
                                <thead>
                                    <tr>
                                        <th>Descrição</th>
                                        <th className="text-right">Análise Vertical (AV)</th>
                                        <th className="text-right">Valor (R$)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {dreData.map((row, index) => (
                                        <tr key={index} className={`dre-row ${row.tipo} ${row.destaque ? 'bold' : ''}`}>
                                            <td className="col-desc">{row.label}</td>
                                            <td className="col-av">{row.tipo !== 'deducao' ? formatPercent(row.av) : '-'}</td>
                                            <td className="col-val">
                                                {/* Adiciona sinal de menos visualmente para despesas */}
                                                {row.tipo === 'despesa' || row.tipo === 'deducao' ? '-' : ''} 
                                                {formatCurrency(row.valor)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <div className="paper-footer">
                                <p>Documento gerado eletronicamente pelo System Hub Financeiro.</p>
                            </div>
                        </div>

                        {/* GRÁFICO LATERAL (Top Custos) */}
                        <div className="dre-charts no-print">
                            <div className="chart-card">
                                <h4>Composição de Custos</h4>
                                <p className="chart-subtitle">Maiores ofensores do resultado</p>
                                <div style={{ width: '100%', height: 350 }}>
                                    <ResponsiveContainer>
                                        <BarChart 
                                            data={dados.detalhamento.filter(d => d.tipo_categoria === 'Despesa').slice(0, 6)} 
                                            layout="vertical"
                                            margin={{ top: 5, right: 30, left: 40, bottom: 5 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                            <XAxis type="number" hide />
                                            <YAxis 
                                                type="category" 
                                                dataKey="categoria" 
                                                width={110} 
                                                tick={{fontSize: 11, fill: '#64748b'}} 
                                            />
                                            <Tooltip 
                                                formatter={(val) => formatCurrency(val)} 
                                                contentStyle={{borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)'}}
                                                cursor={{fill: 'transparent'}}
                                            />
                                            <Bar dataKey="valor_total" radius={[0, 4, 4, 0]} barSize={24}>
                                                {dados.detalhamento.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={index % 2 === 0 ? '#dc3545' : '#e4606d'} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                                <div className="chart-insight">
                                    <strong>Insight:</strong> Suas despesas administrativas representam {
                                        dados.detalhamento.find(d => d.categoria?.includes('Administrativa')) 
                                        ? ((parseFloat(dados.detalhamento.find(d => d.categoria?.includes('Administrativa')).valor_total) / dados.total_despesas) * 100).toFixed(0) 
                                        : '0'
                                    }% dos custos totais.
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            )}
        </div>
    );
}